'use client';
import { useState, useRef, ChangeEvent, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { usePdfTool } from '@/context/PdfToolContext';
import dynamic from 'next/dynamic';
import { useAnalytics } from '@/lib/analytics';

const PdfThumbnail = dynamic(() => import('@/components/PdfThumbnail'), { ssr: false });

interface CompressPDFClientProps {
  h1: string;
  subtitle: string;
  content: string;
}

export default function CompressPDFClient({ h1, subtitle, content }: CompressPDFClientProps) {
  useAnalytics('compress');
  const { items, setItems, resetContext } = usePdfTool();
  const router = useRouter();
  
  const [isDraggingOverall, setIsDraggingOverall] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<string>('recommended');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    resetContext();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [resetContext]);

  const handleFilesAdded = (files: File[]) => {
    setError(null);
    const validFiles = files.filter(f => f.type === 'application/pdf');
    if (validFiles.length !== files.length) {
      setError('Some files were skipped. Only PDF files are supported.');
    }
    
    if (validFiles.length > 0) {
      const newItems = validFiles.map(file => ({
        id: Math.random().toString(36).substring(7),
        file
      }));
      setItems(prev => [...prev, ...newItems]);
    }
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOverall(true);
  };
  const onDragLeave = () => setIsDraggingOverall(false);
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDraggingOverall(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFilesAdded(Array.from(e.dataTransfer.files));
    }
  };

  const handleFileSelect = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      handleFilesAdded(Array.from(e.target.files));
    }
    e.target.value = '';
  };

  const handleStartProcess = () => {
    localStorage.setItem('compress_profile', profile);
    router.push('/compress-pdf/processing');
  };

  const removeFile = (id: string) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };

  return (
    <div className={`container tool-page ${items.length > 0 ? 'app-mode-container' : ''}`}>
      <div className="tool-header text-center">
        <h1>{h1}</h1>
        <p>{subtitle}</p>
      </div>
      
      {error && <div className="error-message" style={{color: 'red', textAlign: 'center', marginBottom: '1rem'}}>{error}</div>}

      <div 
        className="tool-interface"
        style={items.length > 0 ? { display: 'flex', gap: '2rem', alignItems: 'flex-start' } : undefined}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        {/* Left Side: Upload / Grid */}
        <div style={{ flex: 1, maxHeight: '600px', overflowY: 'auto', paddingRight: '1rem', minHeight: '300px' }}>
        {items.length === 0 && (
          <div 
            className={`upload-zone ${isDraggingOverall ? 'dragging' : ''}`}
            onClick={() => fileInputRef.current?.click()}
            style={{ margin: '0 auto' }}
          >
            <div className="upload-icon">📄</div>
            <h3>Select PDFs to compress / Drag & Drop</h3>
          </div>
        )}
        {items.length > 0 && (
            <div className="files-grid" style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: '1rem',
              padding: '1rem',
              background: '#f9fafb',
              borderRadius: '8px',
              minHeight: '300px'
            }}>
              {items.map(item => (
                <div key={item.id} className="pdf-tile" style={{ position: 'relative' }}>
                  <button 
                    className="pdf-tile-remove"
                    onClick={() => removeFile(item.id)}
                  >
                    ✕
                  </button>
                  <div className="pdf-tile-preview">
                    <PdfThumbnail file={item.file} />
                  </div>
                  <div className="pdf-tile-name" title={item.file.name}>{item.file.name}</div>
                </div>
              ))}
              <div 
                className="add-more" 
                onClick={() => fileInputRef.current?.click()}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: 'white', border: '2px dashed #cbd5e1',
                  borderRadius: '6px', cursor: 'pointer', minHeight: '120px'
                }}
              >
                <span style={{fontSize: '2rem', color: '#94a3b8'}}>+</span>
              </div>
            </div>
        )}
        </div>

        {/* Right Side: Settings Panel */}
        {items.length > 0 && (
          <div className="settings-panel" style={{
            width: '350px', 
            background: 'white', 
            boxShadow: '-4px 0 15px rgba(0,0,0,0.05)',
            borderLeft: '1px solid #e2e8f0',
            position: 'fixed',
            top: '75px',
            right: 0,
            bottom: 0,
            height: 'calc(100vh - 75px)',
            zIndex: 50,
            overflowY: 'auto'
          }}>
            <div style={{padding: '1.5rem', borderBottom: '1px solid #e2e8f0', textAlign: 'center'}}>
              <h2 style={{margin: 0, fontSize: '1.2rem', color: '#334155'}}>Compression level</h2>
            </div>
            
            <div className="profile-options">
              <label className={`profile-label ${profile === 'extreme' ? 'active' : ''}`} style={{display: 'block', padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', background: profile === 'extreme' ? '#f1f5f9' : 'transparent'}}>
                <input type="radio" name="profile" value="extreme" checked={profile === 'extreme'} onChange={() => setProfile('extreme')} hidden />
                <div style={{color: '#e53e3e', fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.2rem'}}>EXTREME COMPRESSION</div>
                <div style={{color: '#64748b', fontSize: '0.85rem'}}>Less quality, high compression</div>
              </label>

              <label className={`profile-label ${profile === 'recommended' ? 'active' : ''}`} style={{display: 'block', padding: '1rem 1.5rem', borderBottom: '1px solid #e2e8f0', cursor: 'pointer', background: profile === 'recommended' ? '#f1f5f9' : 'transparent'}}>
                <input type="radio" name="profile" value="recommended" checked={profile === 'recommended'} onChange={() => setProfile('recommended')} hidden />
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <div>
                    <div style={{color: '#e53e3e', fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.2rem'}}>RECOMMENDED COMPRESSION</div>
                    <div style={{color: '#64748b', fontSize: '0.85rem'}}>Good quality, good compression</div>
                  </div>
                  {profile === 'recommended' && <div style={{background: '#10b981', color: 'white', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px'}}>✓</div>}
                </div>
              </label>

              <label className={`profile-label ${profile === 'less' ? 'active' : ''}`} style={{display: 'block', padding: '1rem 1.5rem', cursor: 'pointer', background: profile === 'less' ? '#f1f5f9' : 'transparent'}}>
                <input type="radio" name="profile" value="less" checked={profile === 'less'} onChange={() => setProfile('less')} hidden />
                <div style={{color: '#e53e3e', fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.2rem'}}>LESS COMPRESSION</div>
                <div style={{color: '#64748b', fontSize: '0.85rem'}}>High quality, less compression</div>
              </label>
            </div>

            <div style={{padding: '1.5rem'}}>
              <button 
                className="btn btn-primary btn-large" 
                style={{width: '100%'}}
                onClick={handleStartProcess}
              >
                Compress PDF <span style={{marginLeft: '0.5rem'}}>→</span>
              </button>
            </div>
          </div>
        )}
      </div>

      <input 
        type="file" 
        multiple 
        accept=".pdf,application/pdf"
        className="hidden-input" 
        ref={fileInputRef}
        onChange={handleFileSelect}
      />

      <div className="tool-content" dangerouslySetInnerHTML={{ __html: content }}>
      </div>
    </div>
  );
}
